/**
 * TutaraSelectableCard Component
 *
 * A button-like card component for radio-style selections.
 * Used for time availability, persona selection, and similar patterns.
 */

/* eslint-disable react-refresh/only-export-components */
import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/utils/cn';

const selectableCardVariants = cva(
  'p-3 rounded-lg border-2 text-left transition-all w-full focus:outline-none focus:ring-2 focus:ring-tutara-primary/50 focus:ring-offset-2',
  {
    variants: {
      selected: {
        true: 'border-tutara-primary bg-tutara-primary/10 dark:bg-tutara-primary/20',
        false: 'border-gray-200 dark:border-gray-700 hover:border-tutara-primary/50',
      },
      size: {
        sm: 'p-2',
        md: 'p-3',
        lg: 'p-4',
      },
    },
    defaultVariants: {
      selected: false,
      size: 'md',
    },
  }
);

export interface TutaraSelectableCardProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof selectableCardVariants> {
  /** Whether the card is currently selected */
  selected?: boolean;
  /** Card content */
  children: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
}

const TutaraSelectableCard = React.forwardRef<
  HTMLButtonElement,
  TutaraSelectableCardProps
>(({ selected = false, size, children, className, disabled, ...props }, ref) => {
  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        selectableCardVariants({ selected, size }),
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      aria-pressed={selected}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
});

TutaraSelectableCard.displayName = 'TutaraSelectableCard';

export { TutaraSelectableCard, selectableCardVariants };
